import { create } from 'zustand'
import paymentApi, { PAYMENT_METHODS } from '../services/paymentApi'
import promoApi from '../services/promoApi'

/**
 * Payment Store
 * Zustand store для управления состоянием платежей
 */
const usePaymentStore = create((set, get) => ({
  // Выбранный способ оплаты
  selectedMethod: PAYMENT_METHODS.TELEGRAM_STARS,
  
  // Доступные способы оплаты
  availableMethods: [],
  
  // Недавние успешные способы оплаты (Set of method IDs)
  recentMethodIds: new Set(),
  
  // Пакеты для текущего способа оплаты
  packages: [],
  
  // Состояния загрузки
  isLoadingMethods: false,
  isLoadingPackages: false,
  isProcessingPayment: false,
  
  // ID пакета, который сейчас обрабатывается
  processingPackageId: null,
  
  // Ошибки
  methodsError: null,
  packagesError: null,
  paymentError: null,

  // Промо-данные
  bestPromo: null,           // Лучший активный промокод из /api/topup/
  activePromos: [],          // Все активные промокоды пользователя
  promoInputOpen: false,     // Открыта ли модалка ввода промокода
  isActivatingPromo: false,  // Идёт ли активация промокода
  promoActivateError: null,  // Ошибка активации промокода

  /**
   * Установить выбранный способ оплаты
   * @param {string} method - ID способа оплаты
   */
  setSelectedMethod: (method) => {
    console.log('💳 Выбран способ оплаты:', method)
    set({ selectedMethod: method })
    // Автоматически загружаем пакеты для нового способа
    get().loadPackages(method)
  },

  /**
   * Загрузить доступные способы оплаты
   */
  loadAvailableMethods: async () => {
    set({ isLoadingMethods: true, methodsError: null })
    
    try {
      const { methods, recentMethodIds } = await paymentApi.getAvailablePaymentMethods()
      console.log('✅ Загружены доступные способы оплаты:', methods, 'недавние:', [...recentMethodIds])
      set({ 
        availableMethods: methods,
        recentMethodIds,
        selectedMethod: [PAYMENT_METHODS.UZS3, PAYMENT_METHODS.UZS4, PAYMENT_METHODS.UZS6, PAYMENT_METHODS.UZS7, PAYMENT_METHODS.KZT2, PAYMENT_METHODS.KGS1, PAYMENT_METHODS.UAH1, PAYMENT_METHODS.BYN1].includes(get().selectedMethod) && !methods.some((method) => method.id === get().selectedMethod)
          ? (methods[0]?.id || PAYMENT_METHODS.TELEGRAM_STARS)
          : get().selectedMethod,
        isLoadingMethods: false 
      })
    } catch (error) {
      console.error('❌ Ошибка загрузки способов оплаты:', error)
      set({ 
        methodsError: error.message,
        isLoadingMethods: false 
      })
    }
  },

  /**
   * Загрузить пакеты для конкретного способа оплаты
   * @param {string} method - ID способа оплаты
   */
  loadPackages: async (method) => {
    set({ isLoadingPackages: true, packagesError: null })
    
    try {
      const packages = await paymentApi.fetchPackages(method)
      
      // Сортируем пакеты: самый дешевый первый, затем популярные
      const getPackagePrice = (pkg) => {
        // Единый способ получить «цену» для сортировки под разные платежки
        return pkg.stars || pkg.price || pkg.sbp1 || pkg.sbp2 || pkg.crypto1 || pkg.uzs1 || pkg.uzs2 || pkg.uzs3 || pkg.uzs4 || pkg.uzs5 || pkg.uzs6 || pkg.uzs7 || pkg.kzt1 || pkg.kzt2 || pkg.tjs1 || pkg.kgs1 || pkg.uah1 || pkg.byn1 || pkg.card1 || pkg.cardww1 || pkg.cardww2 || pkg.cardww3 || pkg.cardww4 || pkg.cardww5 || pkg.cardww6 || 0
      }

      const minPrice = Math.min(...packages.map(getPackagePrice))

      const sortedPackages = [...packages].sort((a, b) => {
        const aPrice = getPackagePrice(a)
        const bPrice = getPackagePrice(b)

        if (aPrice === minPrice && bPrice !== minPrice) return -1
        if (bPrice === minPrice && aPrice !== minPrice) return 1

        if (a.popular && !b.popular) return -1
        if (b.popular && !a.popular) return 1

        return aPrice - bPrice
      })
      
      console.log(`✅ Загружены пакеты для ${method}:`, sortedPackages)
      set({ 
        packages: sortedPackages,
        isLoadingPackages: false 
      })
    } catch (error) {
      console.error(`❌ Ошибка загрузки пакетов для ${method}:`, error)
      set({ 
        packagesError: error.message,
        isLoadingPackages: false 
      })
    }
  },

  /**
   * Создать платеж
   * @param {Object} packageData - Данные пакета
   * @param {Function} onSuccess - Callback при успехе
   * @param {Function} onError - Callback при ошибке
   */
  createPayment: async (packageData, onSuccess, onError) => {
    const { selectedMethod } = get()
    
    set({ 
      isProcessingPayment: true, 
      processingPackageId: packageData.id,
      paymentError: null 
    })
    
    try {
      console.log(`💰 Создание платежа через ${selectedMethod}:`, packageData)
      
      // Создаем платеж через API
      const paymentData = await paymentApi.createPayment(selectedMethod, packageData)
      
      // Открываем окно оплаты
      paymentApi.openPayment(selectedMethod, paymentData, async (result) => {
        if (result.success) {
          console.log('✅ Платеж успешно завершен')
          set({ 
            isProcessingPayment: false,
            processingPackageId: null 
          })
          if (onSuccess) await onSuccess(result)
        } else {
          console.error('❌ Платеж не завершен:', result.error)
          set({ 
            isProcessingPayment: false,
            processingPackageId: null,
            paymentError: result.error 
          })
          if (onError) onError(result.error)
        }
      })
      
    } catch (error) {
      console.error('❌ Ошибка создания платежа:', error)
      set({ 
        isProcessingPayment: false,
        processingPackageId: null,
        paymentError: error.message 
      })
      if (onError) onError(error.message)
    }
  },

  /**
   * Загрузить данные пополнения с промо-информацией
   * Запрашивает /api/topup/ и сохраняет best_promo и active_promos
   */
  loadTopupData: async () => {
    try {
      const data = await promoApi.fetchTopupWithPromo()
      set({
        bestPromo: data.best_promo,
        activePromos: data.active_promos
      })
    } catch (error) {
 console.error('❌ Ошибка загрузки промо-данных:', error)
    }
  },

  /**
   * Активировать промокод
   * @param {string} code - Код промокода
   * @returns {Promise<boolean>} true при успехе
   */
  activatePromoCode: async (code) => {
    set({ isActivatingPromo: true, promoActivateError: null })

    try {
      await promoApi.activatePromo(code)
      // После успешной активации перезагружаем промо-данные и пакеты
      await get().loadTopupData()
      // Перезагружаем пакеты текущего метода, чтобы обновить promo_bonus_crystals
      const { selectedMethod } = get()
      if (selectedMethod) {
        await get().loadPackages(selectedMethod)
      }
      set({ isActivatingPromo: false })
      return true
    } catch (error) {
      set({
        isActivatingPromo: false,
        promoActivateError: error.message
      })
      return false
    }
  },

  /**
   * Открыть/закрыть модалку ввода промокода
   * @param {boolean} open - Открыть или закрыть
   */
  setPromoInputOpen: (open) => {
    set({ promoInputOpen: open, promoActivateError: null })
  },

  /**
   * Сбросить ошибку активации промокода
   */
  clearPromoError: () => {
    set({ promoActivateError: null })
  },

  /**
   * Сбросить ошибки
   */
  clearErrors: () => {
    set({ 
      methodsError: null,
      packagesError: null,
      paymentError: null 
    })
  },

  /**
   * Сбросить состояние store
   */
  reset: () => {
    set({
      selectedMethod: PAYMENT_METHODS.TELEGRAM_STARS,
      availableMethods: [],
      recentMethodIds: new Set(),
      packages: [],
      isLoadingPackages: false,
      isProcessingPayment: false,
      processingPackageId: null,
      packagesError: null,
      paymentError: null,
      bestPromo: null,
      activePromos: [],
      promoInputOpen: false,
      isActivatingPromo: false,
      promoActivateError: null
    })
  }
}))

export default usePaymentStore
